import React, { useState } from 'react'
import {X, AlertTriangle, FileText, Send} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import toast from 'react-hot-toast'

interface DisputeModalProps {
  task: any
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  userId?: string
}

export function DisputeModal({ task, isOpen, onClose, onSuccess, userId }: DisputeModalProps) {
  const [reason, setReason] = useState('')
  const [description, setDescription] = useState('')
  const [evidence, setEvidence] = useState('')
  const [loading, setLoading] = useState(false)

  if (!isOpen || !task) return null

  const disputeReasons = [
    'Work not completed as specified',
    'Quality below expectations',
    'Missed deadline without communication',
    'Agent not responsive',
    'Deliverables missing',
    'Other'
  ]

  const handleSubmitDispute = async () => {
    if (!userId) {
      toast.error('Please sign in to file dispute')
      return
    }

    if (!reason || !description) {
      toast.error('Please provide reason and description')
      return
    }

    setLoading(true)
    try {
      // Update task status to disputed
      await lumi.entities.tasks.update(task._id, {
        status: 'disputed',
        updatedAt: new Date().toISOString()
      })

      // In real implementation, this would create a dispute record
      // For now, we'll just update the task status

      toast.success('Dispute filed successfully. Our team will review it.')
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Failed to file dispute:', error)
      toast.error('Failed to file dispute')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-slate-800">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6">
          <div className="flex justify-center mb-4">
            <img src="https://static.lumi.new/2d/2db01c8af5f8d23adc3535b856a32a22.webp" alt="XyberMart" className="h-12" />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center border border-red-500/50">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white">File Dispute</h2>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-yellow-300 text-sm">
              <strong>Important:</strong> Filing a dispute will freeze the task and escrow funds. 
              Our arbitration team will review the case and make a decision within 3-5 business days.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Task Details</h3>
            <p className="text-white font-semibold mb-1">{task.title}</p>
            <p className="text-slate-400 text-sm mb-2">{task.description}</p>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500">Budget: <span className="text-white">${task.budget}</span></span>
              <span className="text-slate-500">Deadline: <span className="text-white">{new Date(task.deadline).toLocaleDateString()}</span></span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-3">Dispute Reason *</label>
            <div className="space-y-2">
              {disputeReasons.map((reasonOption) => (
                <label
                  key={reasonOption}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    reason === reasonOption
                      ? 'bg-red-500/20 border-red-500/50'
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reasonOption}
                    checked={reason === reasonOption}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-4 h-4 text-red-500 focus:ring-red-500"
                  />
                  <span className="text-white">{reasonOption}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Detailed Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              placeholder="Please provide a detailed explanation of the issue..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <p className="text-slate-500 text-xs mt-1">
              Be specific about what went wrong and why you believe the dispute is warranted.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Evidence / Supporting Information
            </label>
            <textarea
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              rows={3}
              placeholder="Links to screenshots, messages, or other evidence..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 bg-slate-800 text-white py-3 rounded-lg font-semibold hover:bg-slate-700 transition-all border border-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitDispute}
              disabled={loading || !reason || !description}
              className="flex-1 bg-gradient-to-r from-red-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-red-500/50 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              {loading ? 'Submitting...' : 'Submit Dispute'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}